import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {getAggregateErrorHandler} from './errorHandlers';
import {
  ApiOperations,
  ApiOperationStatus,
  PartialData,
  QueryParameters,
} from '@di-nxt/resource-container';
import {EnhancedErrorHandler, ApiOperationsWithErrorHandler} from './types';

export const createUseWithErrorHandler = <
  TData extends PartialData<TIdKey, any>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Record<string, unknown>,
  TIdKey extends keyof TData = keyof TData
>(
  useApiOperations: () => ApiOperations<TData, TParameters, TPayload, TIdKey>,
): (() => ApiOperationsWithErrorHandler<
  TData,
  TParameters,
  TPayload,
  TIdKey
>) => {
  return () => {
    const dispatch = useDispatch();
    const generalErrorHandler = useCallback(
      (error: Error, errorHandler?: EnhancedErrorHandler) => {
        const handler = getAggregateErrorHandler(errorHandler, dispatch);
        handler(error);
        return ApiOperationStatus.Failed;
      },
      [dispatch],
    );

    const apiOperations = useApiOperations();

    return {
      getItem: (id: TData[TIdKey], errorHandler?: EnhancedErrorHandler) =>
        apiOperations.getItem(id).catch(error => {
          generalErrorHandler(error, errorHandler);
        }),
      getList: (
        parameters?: TParameters,
        errorHandler?: EnhancedErrorHandler,
      ) =>
        apiOperations.getList(parameters).catch(error => {
          generalErrorHandler(error, errorHandler);
        }),
      update: (
        id: TData[TIdKey],
        data: Omit<TData, TIdKey>,
        errorHandler?: EnhancedErrorHandler,
      ) =>
        apiOperations
          .update(id, data)
          .catch(error => generalErrorHandler(error, errorHandler)),
      remove: (id: TData[TIdKey], errorHandler?: EnhancedErrorHandler) =>
        apiOperations
          .remove(id)
          .catch(error => generalErrorHandler(error, errorHandler)),
      create: (payload: TPayload, errorHandler?: EnhancedErrorHandler) =>
        apiOperations
          .create(payload)
          .catch(error => ({status: generalErrorHandler(error, errorHandler)})),
    };
  };
};
